import React from 'react';
import { LocationNode, NodeId, ScopeStats } from './types';

interface LocationNodeRowProps {
  node: LocationNode;
  isFocused: boolean;
  stats: ScopeStats;
  onFocus: (id: NodeId) => void;
  onSelectAllInScope: (id: NodeId) => void;
  onClearScope: (id: NodeId) => void;
}

const LocationNodeRow: React.FC<LocationNodeRowProps> = ({
  node,
  isFocused,
  stats,
  onFocus,
  onSelectAllInScope,
  onClearScope,
}) => {
  const badgeClass =
    stats.state === 'all'
      ? 'badge bg-info text-white'
      : stats.state === 'partial'
      ? 'badge bg-info-subtle text-info-emphasis'
      : 'badge bg-light text-muted';

  const iconClass = node.type === 'group' ? 'fa-building' : 'fa-flask';

  return (
    <div
      className={`device-selector__node device-selector__node--${node.type} ${
        isFocused ? 'is-focused' : ''
      }`}
      role="treeitem"
      aria-selected={isFocused}
    >
      <button
        type="button"
        className="device-selector__node-name btn btn-link p-0 text-decoration-none flex-grow-1 text-start text-truncate"
        onClick={() => onFocus(node.id)}
        title={node.name}
      >
        <i className={`fas ${iconClass} me-2 text-muted`}></i>
        {node.name}
      </button>
      <span className={`${badgeClass} ms-2 device-selector__count`}>
        {stats.selected}/{stats.total}
      </span>
      <div className="device-selector__node-actions ms-1">
        {stats.state === 'all' ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-danger"
            onClick={(e) => {
              e.stopPropagation();
              onClearScope(node.id);
            }}
            title="Clear all in this scope"
          >
            Clear
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={(e) => {
              e.stopPropagation();
              onSelectAllInScope(node.id);
            }}
            title="Select all in this scope"
          >
            All
          </button>
        )}
      </div>
    </div>
  );
};

export interface LocationTreeProps {
  nodes: LocationNode[];
  rootIds: NodeId[];
  focusedNodeId: NodeId | null;
  rootStats: ScopeStats;
  computeStats: (nodeId: NodeId) => ScopeStats;
  onFocus: (id: NodeId | null) => void;
  onSelectAllInScope: (id: NodeId | null) => void;
  onClearScope: (id: NodeId | null) => void;
}

export const LocationTree: React.FC<LocationTreeProps> = ({
  nodes,
  rootIds,
  focusedNodeId,
  rootStats,
  computeStats,
  onFocus,
  onSelectAllInScope,
  onClearScope,
}) => {
  const nodesById = React.useMemo(() => {
    const map = new Map<NodeId, LocationNode>();
    nodes.forEach((n) => map.set(n.id, n));
    return map;
  }, [nodes]);

  const renderSubtree = (id: NodeId, seen: Set<NodeId>): React.ReactNode => {
    if (seen.has(id)) return null;
    const node = nodesById.get(id);
    if (!node) return null;
    const nextSeen = new Set(seen);
    nextSeen.add(id);
    // A Group may only nest Labs (and a Lab nests nothing visible — devices
    // are leaves and intentionally absent from nodesById). This guards against
    // id collisions between groups and labs that would otherwise render the
    // wrong-type node as a child.
    const visibleChildren = node.childIds.filter((cid) => {
      if (node.type !== 'group') return false;
      const child = nodesById.get(cid);
      return child?.type === 'lab';
    });
    return (
      <li key={id}>
        <LocationNodeRow
          node={node}
          isFocused={focusedNodeId === id}
          stats={computeStats(id)}
          onFocus={onFocus}
          onSelectAllInScope={onSelectAllInScope}
          onClearScope={onClearScope}
        />
        {visibleChildren.length > 0 && (
          <ul className="list-unstyled mb-0">
            {visibleChildren.map((childId) => renderSubtree(childId, nextSeen))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav className="device-selector__tree" aria-label="Locations">
      <div className="device-selector__tree-header text-muted text-uppercase small px-2 py-1">
        Locations
      </div>
      <ul className="list-unstyled mb-0" role="tree">
        <li>
          <div
            className={`device-selector__node device-selector__node--all ${
              focusedNodeId === null ? 'is-focused' : ''
            }`}
            role="treeitem"
            aria-selected={focusedNodeId === null}
          >
            <button
              type="button"
              className="device-selector__node-name btn btn-link p-0 text-decoration-none flex-grow-1 text-start"
              onClick={() => onFocus(null)}
            >
              <i className="fas fa-globe me-2 text-muted"></i>
              All
            </button>
            <span
              className={
                rootStats.state === 'all'
                  ? 'badge bg-info text-white ms-2 device-selector__count'
                  : rootStats.state === 'partial'
                  ? 'badge bg-info-subtle text-info-emphasis ms-2 device-selector__count'
                  : 'badge bg-light text-muted ms-2 device-selector__count'
              }
            >
              {rootStats.selected}/{rootStats.total}
            </span>
            <div className="device-selector__node-actions ms-1">
              {rootStats.state === 'all' ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-danger"
                  onClick={() => onClearScope(null)}
                >
                  Clear
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => onSelectAllInScope(null)}
                >
                  All
                </button>
              )}
            </div>
          </div>
        </li>
        {rootIds.map((id) => renderSubtree(id, new Set()))}
      </ul>
    </nav>
  );
};
